import React, {useEffect, useState} from "react";
import {useField, useFormikContext} from "formik";
import TextField from "@mui/material/TextField";
import {getBrowserLocale} from "../utility";
import {useAuthsState} from "../auths/authsContext";
import {formatDateStr} from "./formatDateStr";

/**
 * Component used to register dates either as year-only or full date.
 * @param formikKey
 * @param onChangeCallback
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
export const FormikDateField = ({formikKey, onChangeCallback, ...props}) => {
    const {userData} = useAuthsState();
    const {setFieldValue, setFieldError} = useFormikContext();
    const [field] = useField(formikKey);
    const [currentDate, setCurrentDate] = useState(field.value || props.defaultValue || '');
    const [error, setError] = useState(false);
    const [yearOnly, setYearOnly] = useState(false);

    // Fetch the user's locale, fallback to the browser's locale.
    const currentLocale = userData.locale || getBrowserLocale(true);
    const norwegianUser = ['no', 'nn', 'nb'].some(value => currentLocale.includes(value));

    const dateformatRegEx = norwegianUser
        ? /^((\d{2}.)(\d{2}.))?(\d{4})$/gi // dd.mm.yyyy
        : /^(\d{4})((-\d{2})(-\d{2}))?$/gi; // yyyy-mm-dd

    const targetToDate = (value) => {
        const parts = norwegianUser ? value.split(".") : value.split("-");
        if (parts.length === 1) {
            // Assume year only
            setYearOnly(true);
            return new Date(Date.UTC(parts[0], 0, 1, 0, 0, 0));
        } else {
            setYearOnly(false);
            if (norwegianUser) {
                return new Date(Date.UTC(parts[2], parts[1] - 1, parts[0], 0, 0, 0));
            } else {
                return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 0, 0, 0));
            }
        }
    };

    const onChange = (event) => {
        if (event.target.value === "") {
            setFieldValue(field.name, null);
            setError(false);
        } else if (!event.target.value.match(dateformatRegEx)) {
            setError(true);
            setFieldError(field.name, '');
        } else {
            const d = targetToDate(event.target.value);
            field.onChange(event.target.value);
            setFieldValue(field.name, d);
            setError(false);
        }
        setCurrentDate(event.target.value);

        if (onChangeCallback) {
            onChangeCallback(event.target.value);
        }
    };

    /**
     * Hook used to correctly format the date values.
     * Using field.value (string/Date scalar) instead of the whole field object
     * avoids the expensive deep-comparison of useDeepCompareEffect.
     */
    useEffect(() => {
        if (field.value && field.value !== 'delete') {
            const loadedStr = field.value.toString();
            const yearOnlyMask = /^(\d{4}-01-01)T00:00:00.000Z$/gi;
            const m = loadedStr.match(yearOnlyMask);
            if (yearOnly) {
                const yr = new Date(loadedStr).getFullYear();
                setCurrentDate(yr);
                field.onChange(loadedStr);
            } else if (m) {
                // Handle year-only value.
                const y = loadedStr.match(/^(\d{4})/gi)[0];
                setCurrentDate(y);
                field.onChange(y);
            } else {
                // Handle full date.
                const fullDateStr = formatDateStr(norwegianUser, loadedStr);
                setCurrentDate(fullDateStr); // Sets the field value!
                field.onChange(fullDateStr);
            }
        } else if (field.value === 'delete') {
            // If the field is "deleted", reset the currentDate value.
            setCurrentDate('');
        } else if (!field.value) {
            setCurrentDate('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, yearOnly, norwegianUser]);

    return (
        (<TextField
            key={field.name}
            id={field.name}
            name={field.name}
            variant={"outlined"}
            error={error}
            value={currentDate}
            onChange={onChange}
            onBlur={field.onBlur}
            {...props}
            slotProps={{
                htmlInput: {pattern: "\\d{0,2}.?\\d{0,2}.?\\d{4}"}
            }}/>)
    );
};
